import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { Separator } from "@/components/ui/separator";

const Cart = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeFromCart, totalPrice, totalItems } = useCart();

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-grow pt-20">
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-2xl mx-auto text-center animate-fade-in">
              <ShoppingBag className="h-24 w-24 mx-auto mb-6 text-muted-foreground" />
              <h1 className="text-4xl font-bold text-primary mb-4">
                Coșul tău este gol
              </h1>
              <p className="text-muted-foreground text-lg mb-8">
                Explorează catalogul nostru și adaugă produse în coș
              </p>
              <Button size="lg" onClick={() => navigate("/magazin")}>
                Către magazin
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-grow pt-20 bg-muted/30">
        <div className="container mx-auto px-4 py-12">
          <h1 className="text-4xl font-bold text-primary mb-8 animate-fade-in">
            Coșul meu ({totalItems} {totalItems === 1 ? "produs" : "produse"})
          </h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {items.map((item, index) => (
                <Card
                  key={item.id}
                  className="overflow-hidden animate-fade-in hover-scale"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      <div
                        className="relative w-32 h-32 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer group"
                        onClick={() => navigate(`/produs/${item.slug}`)}
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>

                      <div className="flex-grow">
                        <h3
                          className="text-xl font-semibold text-foreground mb-2 cursor-pointer hover:text-primary transition-colors"
                          onClick={() => navigate(`/produs/${item.slug}`)}
                        >
                          {item.name}
                        </h3>
                        
                        <p className="text-2xl font-bold text-primary mb-4">
                          {item.price.toFixed(2)} RON
                        </p>

                        <div className="flex items-center gap-4">
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                updateQuantity(item.id, item.quantity - 1)
                              }
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-12 text-center font-semibold">
                              {item.quantity}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                updateQuantity(item.id, item.quantity + 1)
                              }
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Remove Button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => removeFromCart(item.id)}
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>

                      {/* Subtotal */}
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground mb-1">
                          Subtotal
                        </p>
                        <p className="text-2xl font-bold text-primary">
                          {(item.price * item.quantity).toFixed(2)} RON
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24 animate-fade-in">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-primary mb-6">
                    Sumar comandă
                  </h2>

                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-lg">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-semibold">
                        {totalPrice.toFixed(2)} RON
                      </span>
                    </div>
                    
                    <div className="flex justify-between text-lg">
                      <span className="text-muted-foreground">Transport</span>
                      <span className="font-semibold text-green-600">
                        GRATUIT
                      </span>
                    </div>

                    <Separator />

                    <div className="flex justify-between text-xl">
                      <span className="font-bold">Total</span>
                      <span className="font-bold text-primary">
                        {totalPrice.toFixed(2)} RON
                      </span>
                    </div>
                  </div>

                  <Button
                    size="lg"
                    className="w-full text-lg py-6 mb-4"
                    onClick={() => navigate("/checkout")}
                  >
                    Finalizează comanda
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full"
                    onClick={() => navigate("/magazin")}
                  >
                    Continuă cumpărăturile
                  </Button>

                  <div className="mt-6 pt-6 border-t space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Garanție calitate 100%</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span>Plată la livrare disponibilă</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Cart;
